// ───────────────────────────────────────────────────────────────────────────
// 🔑 TEST ACCOUNTS (Jesse, 2026-10-20): on a borrower whose address is on the test-account list only — set or clear
// is_demo, and "Accept documents (test account)" (the step-5 skip, is_demo on step 5). Both are staff 1 and 51 only
// (the server refuses everyone else, and any address not on the list); each asks for a reason, which is recorded with
// the staff id. Neither touches credit: steps 6 and 7 stay the borrower's own clicks. backend/utils/testAccountBypass.
// ───────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function TestAccountCard({ borrowerId, token }) {
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState(null);          // 'demo-on' | 'demo-off' | 'accept'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => axios.get(`${API}/api/admin/borrowers/${borrowerId}/test-account`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setInfo(r.data)).catch(() => setInfo(null)), [borrowerId, token]);
  useEffect(() => { load(); }, [load]);
  if (!info || !info.listed) return null;          // a real borrower never sees this card

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      if (mode === 'accept') {
        const r = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/test-account/accept-documents`, { reason }, { headers });
        setResult(r.data.message || 'Done.');
      } else if (mode === 'sla-on' || mode === 'sla-off') {
        await axios.post(`${API}/api/admin/borrowers/${borrowerId}/test-account/sla-alerts`, { on: mode === 'sla-on', reason }, { headers });
        setResult(mode === 'sla-on' ? 'SLA alerts on this test account go to the assigned staff.' : 'SLA alerts on this test account go to the tester only.');
      } else {
        await axios.post(`${API}/api/admin/borrowers/${borrowerId}/test-account/demo-flag`, { on: mode === 'demo-on', reason }, { headers });
        setResult(mode === 'demo-on' ? 'This is now a demo test account.' : 'The demo flag is cleared.');
      }
      setMode(null); setReason(''); load();
    } catch (e) {
      setResult(e?.response?.data?.error || 'That did not go through.');
    } finally { setBusy(false); }
  };

  const canAccept = info.is_demo && info.current_step === 5;
  return (
    <div data-testid="admin-test-account" className="bg-white rounded-xl border border-amber-200 p-4 mt-4">
      <div className="text-sm font-semibold text-gray-800 mb-1">Test account</div>
      <div className="text-xs text-gray-500 mb-2">
        On the test-account list. {info.is_demo ? 'Demo: credit is the canned fixture, email is redirected.' : 'Not a demo account.'}
        {info.current_step ? ` On step ${info.current_step}.` : ''}
        {/* 🔑 Enforced on every account (backend utils/fraudFlagPlacement, Jesse 2026-10-31): no toggle; flags are labelled. */}
        <div data-testid="admin-test-account-fraud" className="mt-1">
          Fraud checks: enforced, as on every file. Flags hold the pull and the decision, and are labelled TEST ACCOUNT.
        </div>
        {/* 🔑 SLA alerts (backend utils/sectionReviewState, Jesse 2026-10-27). */}
        <div data-testid="admin-test-account-sla" className="mt-1">
          {info.sla_alerts_to_staff && !info.is_demo ? 'SLA alerts: to the assigned staff.' : 'SLA alerts: to the tester only (hannah@), not to the assigned staff.'}
        </div>
      </div>
      {!info.can_act ? (
        <div className="text-xs text-gray-500">Only Hannah and Jesse can change this account.</div>
      ) : mode ? (
        <div>
          <textarea data-testid="admin-test-account-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder="Why (recorded with your name)" />
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid="admin-test-account-confirm" disabled={busy || reason.trim().length < 10} onClick={submit}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#3a6ad9] disabled:bg-gray-300">
              {mode === 'accept' ? 'Accept documents' : mode === 'demo-on' ? 'Make demo'
                : mode === 'sla-on' ? 'Send SLA alerts to staff' : mode === 'sla-off' ? 'Send SLA alerts to the tester only' : 'Clear demo'}
            </button>
            <button type="button" onClick={() => setMode(null)} className="px-3 py-1.5 rounded-lg text-sm border border-gray-300">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="admin-test-account-demo" onClick={() => setMode(info.is_demo ? 'demo-off' : 'demo-on')}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-800">{info.is_demo ? 'Clear demo flag…' : 'Make demo test account…'}</button>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" data-testid="admin-test-account-sla-alerts" checked={!!info.sla_alerts_to_staff} disabled={!!info.is_demo}
              onChange={() => setMode(info.sla_alerts_to_staff ? 'sla-off' : 'sla-on')} />
            Send SLA alerts to the assigned staff{info.is_demo ? ' (not on a demo account)' : ''}
          </label>
          <button type="button" data-testid="admin-test-account-accept" disabled={!canAccept} onClick={() => setMode('accept')}
            title={canAccept ? '' : 'Needs a demo account on step 5'}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-800 disabled:text-gray-400">Accept documents (test account)…</button>
        </div>
      )}
      {result && <div className="text-xs text-gray-700 mt-2">{result}</div>}
    </div>
  );
}
